import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { RoleGuard } from './role-guard';
import { PermissionService } from '../services/permission.service';
import { AppLoadingService } from '../services/app-loading.service';

export const authGuard: CanActivateFn = async (route, state) => {
  if (!environment.authEnabled) return true;

  const msalGuard = inject(MsalGuard);
  const roleGuard = inject(RoleGuard);
  const permissionService = inject(PermissionService);
  const router = inject(Router);
  const loading = inject(AppLoadingService);

  const msalResult = await firstValueFrom(msalGuard.canActivate(route, state));
  if (msalResult !== true) return msalResult;

  const roleResult = await roleGuard.canActivate();
  if (!roleResult) return false;

  if (!permissionService.loaded()) {
    const showSpinner = setTimeout(() => {
      loading.loading.set(true);
      loading.message.set('Loading profile');
    }, 1500);

    const escalate = setTimeout(() => {
      loading.message.set('Database is waking up, this may take a moment');
    }, 8000);

    try {
      await firstValueFrom(permissionService.load());
    } finally {
      // Always clear the overlay — redirect paths below never come back through here,
      // and a stale loading=true covers the destination page indefinitely.
      clearTimeout(showSpinner);
      clearTimeout(escalate);
      loading.loading.set(false);
      loading.message.set('');
    }
  }

  if (!permissionService.loaded()) {
    if (permissionService.loadFailure() === 'transient') {
      return router.createUrlTree(['/waking-up'], { queryParams: { returnUrl: state.url } });
    }
    return router.createUrlTree(['/login-failed']);
  }

  if (!permissionService.isProvisioned()) {
    return router.createUrlTree(['/not-authorized']);
  }

  return true;
};
